import React, { useState } from 'react';
import Icon from '../../common/icons';
import api from '../../../utils/api';
import './TestNoteBox.css';

/**
 * "Anything you want the guidance office to know?" on a test's finish card (L7).
 *
 * Optional: Skip hides it, and nothing here holds up Done or the other finish buttons. The
 * note goes to POST /api/test-note/ once per finished test; the guidance office reads it in
 * Review and release. Anna never answers it, and whatever the office sees about it (how
 * worrying it looks) never comes back here: after sending there is only a thank-you.
 * Offline it says it couldn't send and keeps the text for another try (no outbox).
 */
export const NOTE_MAX = 500;

const tx = (lang, en, fil) => (lang === 'fil' ? fil : en);

const TestNoteBox = ({ testKey, sessionId, school, lang = 'en' }) => {
  const [text, setText] = useState('');
  const [state, setState] = useState('open'); // open | sending | failed | sent | skipped

  if (!sessionId || state === 'skipped') return null;

  if (state === 'sent') {
    return (
      <div className="ts-notebox is-sent" role="status">
        <span className="ts-notebox-done" aria-hidden="true">
          <Icon name="check" size={16} />
        </span>
        <p>{tx(lang, 'Thanks. The guidance office will read this.', 'Salamat. Babasahin ito ng guidance office.')}</p>
      </div>
    );
  }

  const send = async () => {
    const note = text.trim();
    if (!note || state === 'sending') return;
    setState('sending');
    try {
      await api.post(
        '/api/test-note/',
        { assessment_type: testKey, session_id: sessionId, text: note },
        { skipErrorToast: true, timeout: 20000 },
      );
      setState('sent');
    } catch (error) {
      // Already sent (another tab, or a retry after a reply that got lost): it's there
      setState(error?.response?.status === 409 ? 'sent' : 'failed');
    }
  };

  const office = school
    ? tx(lang, `The ${school} guidance office will read this.`, `Babasahin ito ng ${school} guidance office.`)
    : tx(lang, 'The guidance office will read this.', 'Babasahin ito ng guidance office.');
  const busy = state === 'sending';
  const left = NOTE_MAX - text.length;

  return (
    <div className="ts-notebox">
      <label className="ts-notebox-label" htmlFor={`ts-note-${testKey}`}>
        {tx(lang, 'Anything you want the guidance office to know?', 'May gusto ka bang ipaalam sa guidance office?')}{' '}
        <span>{tx(lang, '(optional)', '(hindi kailangan)')}</span>
      </label>
      <textarea
        id={`ts-note-${testKey}`}
        className="ts-notebox-input"
        rows={3}
        maxLength={NOTE_MAX}
        value={text}
        disabled={busy}
        onChange={(event) => {
          setText(event.target.value);
          if (state === 'failed') setState('open');
        }}
        placeholder={tx(lang, 'Write it here', 'Isulat dito')}
        aria-describedby={`ts-note-${testKey}-help`}
      />
      <div className="ts-notebox-meta">
        <span id={`ts-note-${testKey}-help`}>
          {office} {tx(lang, 'Anna won’t reply to it here.', 'Hindi ito sasagutin ni Anna dito.')}
        </span>
        <span className={`ts-notebox-count${left < 50 ? ' is-low' : ''}`} aria-live="polite">
          {text.length} / {NOTE_MAX}
        </span>
      </div>
      {state === 'failed' && (
        <p className="ts-notebox-error" role="alert">
          <Icon name="wifi-off" size={15} />
          {tx(lang,
            'Your note didn’t send. Check your internet and try again.',
            'Hindi naipadala ang note mo. Tingnan ang internet mo at subukan ulit.')}
        </p>
      )}
      <div className="ts-notebox-actions">
        <button type="button" className="ts-notebox-send" onClick={send} disabled={!text.trim() || busy}>
          <Icon name={state === 'failed' ? 'refresh' : 'send'} size={16} />
          <span>
            {busy
              ? tx(lang, 'Sending…', 'Ipinapadala…')
              : state === 'failed'
                ? tx(lang, 'Try again', 'Subukan ulit')
                : tx(lang, 'Send note', 'Ipadala')}
          </span>
        </button>
        <button type="button" className="ts-notebox-skip" onClick={() => setState('skipped')} disabled={busy}>
          {tx(lang, 'Skip', 'Laktawan')}
        </button>
      </div>
    </div>
  );
};

export default TestNoteBox;
